import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink,
  BreadcrumbSeparator, BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import {
  AlertDialog, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus, Search, AlertTriangle, ExternalLink, Trash2, ShieldAlert, Loader2, Users,
  ChevronRight, Trophy, Globe, MapPin, Building2, Landmark, Home, UsersRound, UserX,
  Upload,
} from 'lucide-react';
import {
  adminDeletePlayerProfile, adminListLinkSuggestions, adminListPlayerProfiles,
  adminListSportsCatalog, adminListClubsCatalog, adminListTeamsCatalog,
  friendlyError,
} from '@/lib/api';
import { resolvePlayerAvatar } from '@/lib/playerAvatar';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]',
  pending_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]',
  hidden: 'bg-[var(--ns-bg-2)] text-[var(--ns-text-subtle)]',
  disabled: 'bg-[var(--ns-red)]/15 text-[var(--ns-red)]',
};
const STATUS_LABEL = {
  draft: 'Borrador', pending_review: 'Pendiente revisión',
  approved: 'Aprobado', hidden: 'Oculto', disabled: 'Desactivado',
};

// Niveles jerárquicos oficiales NetScouters. Cada nivel resuelve su valor
// desde el catálogo del club/equipo real; no hay listas hardcoded.
const LEVELS = [
  { key: 'sport', label: 'Deporte', icon: Trophy, empty: 'Sin deporte' },
  { key: 'country', label: 'País', icon: Globe, empty: 'Sin país' },
  { key: 'region', label: 'Comunidad / Región', icon: MapPin, empty: 'Sin comunidad' },
  { key: 'province', label: 'Provincia', icon: Landmark, empty: 'Sin provincia' },
  { key: 'city', label: 'Ciudad / Localidad', icon: Home, empty: 'Sin ciudad' },
  { key: 'club', label: 'Club', icon: Building2, empty: 'Sin club' },
  { key: 'team', label: 'Equipo / Categoría', icon: UsersRound, empty: 'Sin equipo' },
];

/**
 * Mapeo mínimo categoría (BBDD) → etiqueta legible para tarjetas.
 * Cubre las categorías de fútbol usadas hoy; el fallback capitaliza la
 * primera letra para cualquier otra categoría futura sin necesidad de
 * editar este componente.
 */
const CATEGORY_LABELS = {
  senior: 'Senior',
  juvenil: 'Juvenil',
  cadete: 'Cadete',
  infantil: 'Infantil',
  alevin: 'Alevines',
  benjamin: 'Benjamines',
  prebenjamin: 'Prebenjamines',
  querubin: 'Querubines',
};
function categoryLabel(cat, fallback) {
  if (!cat) return fallback || null;
  const key = String(cat).toLowerCase();
  return CATEGORY_LABELS[key] || (key.charAt(0).toUpperCase() + key.slice(1));
}

/**
 * Construye para cada ficha su ruta jerárquica siguiendo la estructura
 * territorial/deportiva. Todos los campos provienen del club (ya guarda
 * country/region/province/city), del sport y del team. Fichas sin club o
 * sin team caen al bucket especial `orphans`.
 */
function buildPath(profile, sportsMap, clubsMap, teamsMap) {
  const club = profile.club_id ? clubsMap[profile.club_id] : null;
  const team = profile.team_id ? teamsMap[profile.team_id] : null;
  const sport = profile.sport_id ? sportsMap[profile.sport_id] : null;
  if (!club || !team) return null;
  return {
    sport: { id: sport?.id || profile.sport_id || '__none__', label: sport?.name || 'Sin deporte' },
    country: { id: club.country || '__none__', label: club.country || 'Sin país' },
    region: { id: club.autonomous_community_or_region || '__none__', label: club.autonomous_community_or_region || 'Sin comunidad' },
    province: { id: club.province || '__none__', label: club.province || 'Sin provincia' },
    city: { id: club.city || '__none__', label: club.city || 'Sin ciudad' },
    club: { id: club.id, label: club.name },
    team: {
      id: team.id,
      // Título principal: categoría en formato legible (Senior/Benjamines/…).
      // Si no existe categoría, se cae al nombre completo del equipo para no
      // dejar la tarjeta sin identificación.
      label: categoryLabel(team.category, team.name),
      // Subtítulo: temporada (2024-2025, 2025-2026, …). El componente
      // BucketGrid lo pinta debajo del título cuando está presente.
      subtitle: team.season || null,
      category: team.category,
    },
  };
}

export default function AdminPlayerProfilesList() {
  const [profiles, setProfiles] = useState([]);
  const [sports, setSports] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [pendingSugg, setPendingSugg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [committedQuery, setCommittedQuery] = useState('');
  // Ruta actual del drill-down: { sport?, country?, region?, province?, city?, club?, team? }
  const [path, setPath] = useState({});
  // Estado del flujo de eliminación
  const [delState, setDelState] = useState(null);

  const load = async (q = '') => {
    setLoading(true);
    try {
      const [list, sugg, sportsList, clubsList, teamsList] = await Promise.all([
        adminListPlayerProfiles(q ? { query: q, page_size: 100 } : { page_size: 100 }),
        adminListLinkSuggestions({ status: 'pending' }),
        adminListSportsCatalog(),
        adminListClubsCatalog(),
        adminListTeamsCatalog(),
      ]);
      setProfiles(list.items || []);
      setPendingSugg(sugg.pending_count || 0);
      setSports(sportsList.items || []);
      setClubs(clubsList.items || []);
      setTeams(teamsList.items || []);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(''); /* eslint-disable-next-line */ }, []);

  const sportsMap = useMemo(() => Object.fromEntries(sports.map((s) => [s.id, s])), [sports]);
  const clubsMap = useMemo(() => Object.fromEntries(clubs.map((c) => [c.id, c])), [clubs]);
  const teamsMap = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);

  // Enriquecemos cada perfil con su ruta jerárquica y detectamos huérfanos.
  const enriched = useMemo(() => profiles.map((p) => ({
    profile: p, tree: buildPath(p, sportsMap, clubsMap, teamsMap),
  })), [profiles, sportsMap, clubsMap, teamsMap]);
  const orphans = useMemo(() => enriched.filter((e) => !e.tree).map((e) => e.profile), [enriched]);
  const linked = useMemo(() => enriched.filter((e) => e.tree), [enriched]);

  const isSearching = committedQuery.trim().length > 0;

  // Perfiles que pasan la ruta actual (para contar y avanzar niveles).
  const inPath = useMemo(() => {
    if (isSearching) return [];
    return linked.filter(({ tree }) => LEVELS.every((lv) => {
      const sel = path[lv.key];
      return !sel || sel === tree[lv.key].id;
    }));
  }, [linked, path, isSearching]);

  // Determina el siguiente nivel a mostrar según la ruta actual.
  const currentLevelIdx = useMemo(() => {
    for (let i = 0; i < LEVELS.length; i += 1) {
      if (!path[LEVELS[i].key]) return i;
    }
    return LEVELS.length; // ya llegamos al equipo: mostrar jugadores.
  }, [path]);

  // Buckets del nivel actual con conteos.
  const buckets = useMemo(() => {
    if (isSearching || currentLevelIdx >= LEVELS.length) return [];
    const level = LEVELS[currentLevelIdx];
    const map = new Map();
    inPath.forEach(({ tree }) => {
      const node = tree[level.key];
      if (!map.has(node.id)) map.set(node.id, { id: node.id, label: node.label, count: 0, extra: node });
      map.get(node.id).count += 1;
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [inPath, currentLevelIdx, isSearching]);

  // Jugadores visibles cuando llegamos al equipo o cuando se está buscando.
  const visiblePlayers = useMemo(() => {
    if (isSearching) return enriched.map((e) => e.profile);
    if (currentLevelIdx < LEVELS.length) return [];
    return inPath.map((e) => e.profile);
  }, [inPath, currentLevelIdx, isSearching, enriched]);

  const goTo = (levelKey, id) => {
    // Restablece niveles inferiores.
    const idx = LEVELS.findIndex((lv) => lv.key === levelKey);
    setPath((prev) => {
      const next = { ...prev };
      for (let i = idx; i < LEVELS.length; i += 1) delete next[LEVELS[i].key];
      next[levelKey] = id;
      return next;
    });
  };
  const goRoot = () => setPath({});
  const goUpTo = (levelIdx) => {
    setPath((prev) => {
      const next = { ...prev };
      for (let i = levelIdx; i < LEVELS.length; i += 1) delete next[LEVELS[i].key];
      return next;
    });
  };

  // Etiquetas legibles para el breadcrumb (buscando el nombre real).
  const breadcrumbLabels = useMemo(() => LEVELS.map((lv) => {
    const id = path[lv.key];
    if (!id) return null;
    // Buscamos un profile enlazado que coincida con esa selección para leer su etiqueta.
    const sample = linked.find(({ tree }) => tree[lv.key].id === id);
    return sample ? sample.tree[lv.key].label : id;
  }), [linked, path]);

  // ------------- Búsqueda -------------
  const submitSearch = async (e) => {
    e.preventDefault();
    setCommittedQuery(query);
    setPath({});
    await load(query);
  };
  const clearSearch = async () => {
    setQuery('');
    setCommittedQuery('');
    await load('');
  };

  // ------------- Eliminación -------------
  const openDelete = (p) => {
    const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim();
    setDelState({ id: p.id, name: fullName, step: 1, typed: '', deleting: false });
  };
  const cancelDelete = () => setDelState(null);
  const goStep2 = () => setDelState((s) => s ? { ...s, step: 2, typed: '' } : s);
  const canConfirmDelete = !!delState
    && delState.typed.trim().toLowerCase() === (delState.name || '').toLowerCase()
    && (delState.name || '').length > 0;
  const performDelete = async () => {
    if (!canConfirmDelete || delState.deleting) return;
    setDelState((s) => ({ ...s, deleting: true }));
    try {
      await adminDeletePlayerProfile(delState.id);
      toast.success('Ficha eliminada definitivamente.');
      setDelState(null);
      await load(committedQuery);
    } catch (err) {
      toast.error(friendlyError(err));
      setDelState((s) => ({ ...s, deleting: false }));
    }
  };

  return (
    <AdminLayout>
      <div className="flex flex-col gap-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-display uppercase tracking-tight text-2xl sm:text-3xl text-[var(--ns-text)]">Fichas de jugador</h1>
            <p className="text-sm text-[var(--ns-text-muted)] mt-1">
              Navega por Deporte → País → Comunidad → Provincia → Ciudad → Club → Equipo/Categoría.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="outline"
              data-testid="admin-player-profile-import"
              className="h-10 rounded-xl"
            >
              <Link to="/admin/fichas-jugadores/importar" className="inline-flex items-center gap-2">
                <Upload className="h-4 w-4" /> Importar Excel
              </Link>
            </Button>
            <Button
              asChild
              data-testid="admin-player-profile-new"
              className="h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
            >
              <Link to="/admin/fichas-jugadores/nueva" className="inline-flex items-center gap-2">
                <Plus className="h-4 w-4" /> Crear ficha de jugador
              </Link>
            </Button>
          </div>
        </div>

        {pendingSugg > 0 ? (
          <Card
            data-testid="admin-suggestions-banner"
            className="p-4 rounded-2xl bg-[var(--ns-amber-50)] border border-[var(--ns-amber-200)] flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-[var(--ns-gold-700)]" />
              <div>
                <p className="text-sm font-semibold text-[var(--ns-text)]">Posibles fichas pendientes de vinculación</p>
                <p className="text-xs text-[var(--ns-text-muted)]">{pendingSugg} sugerencia(s) pendientes de revisión.</p>
              </div>
            </div>
            <Button asChild variant="outline" data-testid="admin-suggestions-go" className="h-9 rounded-xl">
              <Link to="/admin/sugerencias-vinculacion" className="inline-flex items-center gap-1">
                Revisar <ExternalLink className="h-3 w-3" />
              </Link>
            </Button>
          </Card>
        ) : null}

        {/* Barra de búsqueda global */}
        <Card className="p-4 rounded-2xl bg-white border border-[var(--ns-border)]">
          <form onSubmit={submitSearch} className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre o apellidos"
              className="h-10 bg-white"
              data-testid="admin-player-search"
            />
            <Button
              type="submit"
              data-testid="admin-player-search-submit"
              className="h-10 rounded-xl bg-white border border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)]"
            >
              <Search className="h-4 w-4" />
            </Button>
            {isSearching ? (
              <Button
                type="button"
                variant="outline"
                onClick={clearSearch}
                data-testid="admin-player-search-clear"
                className="h-10 rounded-xl"
              >
                Limpiar
              </Button>
            ) : null}
          </form>
        </Card>

        {/* Breadcrumb del drill-down (sólo cuando no hay búsqueda) */}
        {!isSearching && !loading ? (
          <Card className="px-4 py-3 rounded-2xl bg-white border border-[var(--ns-border)]" data-testid="admin-player-breadcrumb">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  {currentLevelIdx === 0 ? (
                    <BreadcrumbPage className="inline-flex items-center gap-1 font-semibold">
                      <Trophy className="h-3.5 w-3.5 text-[var(--ns-gold-700)]" /> Deportes
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <button type="button" onClick={goRoot} className="inline-flex items-center gap-1 hover:text-[var(--ns-gold-700)]">
                        <Trophy className="h-3.5 w-3.5 text-[var(--ns-gold-700)]" /> Deportes
                      </button>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {LEVELS.map((lv, idx) => {
                  const label = breadcrumbLabels[idx];
                  if (!label) return null;
                  const isLast = idx === currentLevelIdx - 1;
                  return (
                    <React.Fragment key={lv.key}>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem>
                        {isLast ? (
                          <BreadcrumbPage className="font-semibold text-[var(--ns-text)]">{label}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink asChild>
                            <button
                              type="button"
                              onClick={() => goUpTo(idx + 1)}
                              className="hover:text-[var(--ns-gold-700)]"
                            >
                              {label}
                            </button>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          </Card>
        ) : null}

        {loading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
        ) : isSearching ? (
          <PlayerList
            profiles={visiblePlayers}
            onDelete={openDelete}
          />
        ) : currentLevelIdx < LEVELS.length ? (
          <>
            {/* Bloque especial "Sin club/equipo asignado", visible sólo en el nivel raíz */}
            {currentLevelIdx === 0 && orphans.length > 0 ? (
              <Card
                data-testid="admin-player-orphans-bucket"
                className="p-4 rounded-2xl bg-[var(--ns-amber-50)] border border-dashed border-[var(--ns-amber-200)]"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <UserX className="h-5 w-5 text-[var(--ns-gold-700)]" />
                    <div>
                      <p className="text-sm font-semibold text-[var(--ns-text)]">Sin club/equipo asignado</p>
                      <p className="text-xs text-[var(--ns-text-muted)]">
                        {orphans.length} ficha(s) sin vinculación completa club + equipo.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <PlayerList profiles={orphans} onDelete={openDelete} compact />
                </div>
              </Card>
            ) : null}

            <BucketGrid
              buckets={buckets}
              level={LEVELS[currentLevelIdx]}
              onSelect={(id) => goTo(LEVELS[currentLevelIdx].key, id)}
              emptyMessage={
                buckets.length === 0
                  ? currentLevelIdx === 0
                    ? 'Todavía no hay fichas con club/equipo asignado.'
                    : 'No hay elementos en este nivel para la ruta actual.'
                  : null
              }
            />
          </>
        ) : (
          <PlayerList
            profiles={visiblePlayers}
            onDelete={openDelete}
          />
        )}

        {/* Modal 1: aviso */}
        <AlertDialog
          open={delState?.step === 1}
          onOpenChange={(o) => { if (!o && delState?.step === 1) cancelDelete(); }}
        >
          <AlertDialogContent data-testid="admin-player-list-delete-step1">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-[var(--ns-red)]" />
                Eliminar ficha definitivamente
              </AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción <strong>no se puede deshacer</strong>. Se eliminará la ficha de
                <strong> {delState?.name || 'este jugador'} </strong>
                por completo, junto con sus sugerencias de vinculación.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button type="button" variant="outline" onClick={cancelDelete} className="h-10 rounded-xl">
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={goStep2}
                data-testid="admin-player-list-delete-step1-continue"
                className="h-10 rounded-xl bg-[var(--ns-red)] text-white hover:bg-[var(--ns-red)]/90"
              >
                Continuar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Modal 2: tecleo del nombre */}
        <AlertDialog
          open={delState?.step === 2}
          onOpenChange={(o) => { if (!o && delState?.step === 2) cancelDelete(); }}
        >
          <AlertDialogContent data-testid="admin-player-list-delete-step2">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-[var(--ns-red)]">
                <ShieldAlert className="h-5 w-5" />
                Confirmar eliminación definitiva
              </AlertDialogTitle>
              <AlertDialogDescription>
                Para confirmar, escribe el nombre completo del jugador:
                <span className="block mt-2 px-3 py-2 rounded bg-[var(--ns-bg-2)] font-semibold text-[var(--ns-text)] text-center select-none">
                  {delState?.name}
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              value={delState?.typed || ''}
              onChange={(e) => setDelState((s) => s ? { ...s, typed: e.target.value } : s)}
              placeholder="Escribe el nombre completo"
              className="h-10 bg-white mt-2"
              data-testid="admin-player-list-delete-typed"
              autoFocus
            />
            <AlertDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={cancelDelete}
                disabled={!!delState?.deleting}
                className="h-10 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={performDelete}
                disabled={!canConfirmDelete || !!delState?.deleting}
                data-testid="admin-player-list-delete-confirm"
                className="h-10 rounded-xl bg-[var(--ns-red)] text-white hover:bg-[var(--ns-red)]/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {delState?.deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Eliminar definitivamente'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}

/**
 * Rejilla de tarjetas para un nivel jerárquico. Cada bucket representa un
 * valor real del catálogo (deporte, país, club, etc.) y muestra su conteo.
 */
function BucketGrid({ buckets, level, onSelect, emptyMessage }) {
  const Icon = level?.icon || Trophy;
  if (emptyMessage) {
    return (
      <Card className="p-8 rounded-2xl bg-white border border-dashed border-[var(--ns-border-strong)] text-center">
        <p className="text-sm text-[var(--ns-text-muted)]">{emptyMessage}</p>
      </Card>
    );
  }
  return (
    <div
      data-testid={`admin-player-bucket-grid-${level.key}`}
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
    >
      {buckets.map((b) => (
        <button
          key={b.id}
          type="button"
          onClick={() => onSelect(b.id)}
          data-testid={`admin-player-bucket-${level.key}-${b.id}`}
          className="text-left group p-4 rounded-2xl bg-white border border-[var(--ns-border)] hover:border-[var(--ns-gold)]/50 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ns-gold-600)]"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-xl bg-[var(--ns-amber-50)] flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-[var(--ns-gold-700)]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--ns-text-subtle)]">
                  {level.label}
                </p>
                <p className="text-sm font-semibold text-[var(--ns-text)] truncate" title={b.label}>
                  {b.label}
                </p>
                {b.extra?.subtitle ? (
                  <p
                    className="text-[11px] text-[var(--ns-text-muted)] truncate"
                    data-testid={`admin-player-bucket-${level.key}-${b.id}-subtitle`}
                    title={b.extra.subtitle}
                  >
                    {b.extra.subtitle}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className="bg-[var(--ns-bg-2)] text-[var(--ns-text)] text-[11px]">
                {b.count} {b.count === 1 ? 'ficha' : 'fichas'}
              </Badge>
              <ChevronRight className="h-4 w-4 text-[var(--ns-text-subtle)] group-hover:text-[var(--ns-gold-700)]" />
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

/**
 * Lista de fichas de jugador con las acciones tradicionales (Estado, Editar,
 * Directorio y Eliminar). Se reutiliza tanto en el nivel de equipo como en
 * la vista de búsqueda global y en el bucket de "Sin club/equipo asignado".
 */
function PlayerList({ profiles, onDelete, compact = false }) {
  if (!profiles.length) {
    return (
      <Card className="p-8 rounded-2xl bg-white border border-dashed border-[var(--ns-border-strong)] text-center">
        <p className="text-sm text-[var(--ns-text-muted)]">No hay fichas para este filtro.</p>
      </Card>
    );
  }
  return (
    <ul className={compact ? 'space-y-1.5' : 'space-y-2'}>
      {profiles.map((p) => {
        const avatar = resolvePlayerAvatar(p);
        return (
          <li
            key={p.id}
            data-testid={`admin-player-row-${p.id}`}
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-[var(--ns-border)] hover:border-[var(--ns-gold)]/40 transition-colors"
          >
            <div className="h-11 w-11 rounded-xl overflow-hidden bg-[var(--ns-bg-2)] flex items-center justify-center shrink-0">
              {avatar ? (
                <img src={avatar} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="text-xs text-[var(--ns-text-subtle)]">—</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[var(--ns-text)] truncate">
                {p.first_name} {p.last_name}
              </p>
              <p className="text-xs text-[var(--ns-text-muted)] truncate">
                {p.user ? `Vinculado: ${p.user.email}` : 'Sin vincular'} · {p.position || '—'}
              </p>
            </div>
            <Badge
              data-testid={`admin-player-row-status-${p.id}`}
              className={`text-[10px] uppercase ${STATUS_COLORS[p.status] || ''}`}
            >
              {STATUS_LABEL[p.status] || p.status}
            </Badge>
            <Button asChild size="sm" variant="outline" className="h-8 rounded-lg text-xs" data-testid={`admin-player-row-edit-${p.id}`}>
              <Link to={`/admin/fichas-jugadores/${p.id}`}>Editar</Link>
            </Button>
            {p.club_id && p.team_id ? (
              <Button
                asChild
                size="sm"
                variant="outline"
                data-testid={`admin-player-row-directory-${p.id}`}
                className="h-8 rounded-lg text-xs"
                title="Abrir directorio del equipo"
              >
                <Link
                  to={`/admin/clubes/${p.club_id}/equipos/${p.team_id}/directorio`}
                  className="inline-flex items-center gap-1"
                >
                  <Users className="h-3.5 w-3.5" /> Directorio
                </Link>
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(p)}
              data-testid={`admin-player-row-delete-${p.id}`}
              className="h-8 rounded-lg text-xs bg-white border-[var(--ns-red)]/40 text-[var(--ns-red)] hover:bg-[var(--ns-red)]/8 hover:border-[var(--ns-red)]"
              aria-label="Eliminar ficha"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
